import React, { Suspense, useMemo } from 'react'
import { csx, Divider, IconCube, IconStorefront, Text } from '@vtex/admin-ui'
import { graphql } from 'babel-plugin-relay/macro'
import { useSuspenseFragment } from 'react-relay-offline'
import { FormattedMessage, useIntl } from 'react-intl'

import { useProductPageUrlParams } from '../../hooks'
import { mapOffersToProductPrice } from '../../../../core/utils/productSkuOfferConnection'
import type { ProductSkuOtherOffers_Product$key } from './__generated__/ProductSkuOtherOffers_Product.graphql'
import { OfferDeliveryMethodInfo } from './OfferDeliveryMethodInfo'

const productFragment = graphql`
  fragment ProductSkuOtherOffers_Product on Product {
    skus {
      edges {
        node {
          id
          offers(addresses: $addresses) {
            ...productSkuOfferConnection_ProductSkuOfferConnection
          }
        }
      }
    }
  }
`

type ProductSkuRefContentProps = {
  productRef: ProductSkuOtherOffers_Product$key
}

const ProductSkuRefContent = ({ productRef }: ProductSkuRefContentProps) => {
  const { formatMessage } = useIntl()
  const { selectedSkuId } = useProductPageUrlParams()
  const product = useSuspenseFragment(productFragment, productRef)

  const selectedSku = useMemo(() => {
    return product.skus.edges.find(
      (skuEdge) => skuEdge.node.id === selectedSkuId
    )
  }, [product.skus.edges, selectedSkuId])

  if (!selectedSku?.node.offers) {
    return null
  }

  const offers = mapOffersToProductPrice(selectedSku.node.offers)
  const [, ...otherOffers] = offers

  if (otherOffers.length === 0) {
    return null
  }

  const deliveryOffers = otherOffers.filter(
    (offer) => offer.deliveryChannel === 'DELIVERY'
  )

  const pickupOffers = otherOffers.filter(
    (offer) => offer.deliveryChannel === 'PICKUP_IN_POINT'
  )

  const hasOnlyOneDeliveryChannel = offers.every(
    (offer) => offer.deliveryChannel === offers[0]?.deliveryChannel
  )

  return (
    <div
      data-testid="product-sku-other-offers-content"
      className={csx({
        padding: '$space-5',
        border: '$neutral',
        borderRadius: '4px',
      })}
    >
      <Text variant="action1" className={csx({ display: 'block' })}>
        <FormattedMessage id="pdp.other-offers.title" />
      </Text>
      <Text
        variant="action2"
        tone="secondary"
        className={csx({ display: 'block', marginTop: '$space-1' })}
      >
        <FormattedMessage id="pdp.other-offers.hint" />
      </Text>
      <Divider
        className={csx({
          marginTop: '$space-5',
          marginBottom: '$space-4',
        })}
      />
      <div
        className={csx({
          display: 'flex',
        })}
      >
        {deliveryOffers.length > 0 && (
          <OfferDeliveryMethodInfo
            icon={<IconCube />}
            title={formatMessage({ id: 'pdp.sku-availability.delivery.title' })}
            offers={deliveryOffers}
            shouldRenderPrice={!hasOnlyOneDeliveryChannel}
            data-testid="product-sku-other-offers-delivery"
          />
        )}
        {pickupOffers.length > 0 && (
          <OfferDeliveryMethodInfo
            icon={<IconStorefront />}
            title={formatMessage({ id: 'pdp.sku-availability.pickup.title' })}
            offers={pickupOffers}
            shouldRenderPrice={!hasOnlyOneDeliveryChannel}
            data-testid="product-sku-other-offers-pickup"
          />
        )}
      </div>
    </div>
  )
}

export type ProductSkuOtherOffersProps = {
  productRef: ProductSkuOtherOffers_Product$key
}

export const ProductSkuOtherOffers = (props: ProductSkuOtherOffersProps) => (
  <div className={csx({ marginBottom: '$space-5' })}>
    <Suspense fallback={null}>
      <ProductSkuRefContent {...props} />
    </Suspense>
  </div>
)
